import React, { useEffect, useState } from 'react';
import { Project, apiService } from '../services/api';

interface ProjectSelectorProps {
  selectedProject: string | null;
  onProjectSelect: (projectKey: string) => void;
  /**
   * The backend's default project. `undefined` means the backend config has not answered yet:
   * nothing is auto-selected until it does. `null` means there is no default (or the config failed).
   */
  defaultProjectKey: string | null | undefined;
}

const ProjectSelector: React.FC<ProjectSelectorProps> = ({
  selectedProject,
  onProjectSelect,
  defaultProjectKey,
}) => {
  // All hooks must be called before any conditional returns
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDevelopment, setIsDevelopment] = useState(false);

  // Determine if we're in development mode from environment variable
  useEffect(() => {
    const env = process.env.REACT_APP_ENV || 'development';
    const isDevMode = env === 'development';
    setIsDevelopment(isDevMode);
  }, []);

  // Fetch projects and auto-select (works in both dev and production mode)
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const projectsData = await apiService.getProjects();
        setProjects(projectsData);

        // Wait for the backend config. The first run happens at page load, before it has answered; without
        // this guard that run has no default key and picks the first project of the list. The effect runs
        // again when the config arrives.
        if (defaultProjectKey === undefined) return;

        // Auto-select project if none selected
        if (!selectedProject && projectsData.length > 0) {
          // First try the configured default project key from backend
          if (defaultProjectKey) {
            // Check if the default key exists in the fetched projects
            const defaultExists = projectsData.some(p => p.project_key === defaultProjectKey);
            if (defaultExists) {
              onProjectSelect(defaultProjectKey);
              return;
            }
          }
          // Fall back to first project if no default key or default doesn't exist
          onProjectSelect(projectsData[0].project_key);
        }
      } catch (err) {
        console.error('Error loading projects:', err);
        // Fall back to default project key from backend if API fails
        if (defaultProjectKey && !selectedProject) {
          console.log(`✓ Using default project key from backend: ${defaultProjectKey}`);
          onProjectSelect(defaultProjectKey);
          setError(null); // Clear error since we have a fallback
        } else {
          setError('Failed to load projects. Make sure the IE-PAL-Engine backend is running and accessible.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [selectedProject, onProjectSelect, defaultProjectKey]);

  // Show loading state only in development mode
  if (isDevelopment && loading) {
    return (
      <div className="bg-white border-b border-ie-rule p-6" role="status">
        <div className="flex items-center space-x-3">
          <div className="w-5 h-5 bg-ie-cyan rounded-full animate-pulse"></div>
          <span className="text-sm font-medium text-ie-slate">Loading projects...</span>
        </div>
      </div>
    );
  }

  // Show error state only in development mode
  if (isDevelopment && error) {
    return (
      <div className="bg-white border-b-2 border-ie-alert p-6" role="alert">
        <div className="flex items-center space-x-3">
          <div className="w-5 h-5 bg-ie-alert rounded-full flex items-center justify-center">
            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <span className="text-sm font-medium text-ie-alert">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border-b border-ie-rule py-3 md:py-4 pr-4 md:pr-6">
      <div className="flex flex-row items-center gap-3 md:gap-8">
        {/* Invest Europe Logo */}
        <div className="flex-shrink-0 pl-4">
          <img 
            src="/invest-europe-logo-wordmark.png" 
            alt="Invest Europe" 
            className="h-8 md:h-11 w-auto object-contain"
          />
        </div>
        
        {/* Project Selection Section - Only show in development mode */}
        {isDevelopment && (
          <div className="flex flex-row items-center gap-3 flex-1 justify-end md:justify-center min-w-0 pl-2 md:pl-0">
            <div className="flex items-center space-x-3">
              <label htmlFor="project-select" className="hidden sm:block font-condensed text-base md:text-lg font-semibold text-ie-navy">Project</label>
            </div>
            <select
              id="project-select"
              value={selectedProject || ''}
              onChange={(e) => onProjectSelect(e.target.value)}
              className="min-w-0 flex-1 sm:max-w-md px-4 py-2 md:px-5 md:py-2.5 bg-white border border-ie-navy rounded-pill transition-colors duration-200 text-ie-navy truncate"
            >
              <option value="">Choose a project to get started...</option>
              {projects.map((project) => (
                <option key={project.project_key} value={project.project_key}>
                  {project.name}
                </option>
              ))}
            </select>
            {projects.length > 0 && (
              <div className="hidden sm:block px-3 py-1 bg-ie-mist rounded-full">
                <span className="text-xs font-medium text-ie-navy">
                  {projects.length} available
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectSelector;